import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PerformanceGroup } from '@/types/performance-analyze';
import { toast } from 'sonner';

export const usePerformanceGroups = () => {
  const queryClient = useQueryClient();

  const { data: groups, isLoading, refetch } = useQuery({
    queryKey: ['performance-groups'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('performance_analyze_groups')
        .select('*')
        .order('symbol', { ascending: true })
        .order('prefix', { ascending: true })
        .order('strategy', { ascending: true });
      
      if (error) throw error;
      return data as PerformanceGroup[];
    }
  });

  const createGroup = useMutation({
    mutationFn: async (group: Omit<PerformanceGroup, 'id' | 'created_at' | 'updated_at'>) => {
      const { data: userData } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('performance_analyze_groups')
        .insert({
          ...group,
          created_by: userData?.user?.id
        })
        .select()
        .single();
      
      if (error) throw error;

      // Log audit
      await supabase.from('performance_analyze_audit_log').insert({
        action: 'create',
        group_id: data.id,
        new_config: data,
        changed_by: userData?.user?.id
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['performance-groups'] });
      toast.success('Group created successfully');
    },
    onError: (error: any) => {
      toast.error('Failed to create group: ' + error.message);
    }
  });

  const updateGroup = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<PerformanceGroup> & { id: string }) => {
      // Get old config
      const { data: oldData } = await supabase
        .from('performance_analyze_groups')
        .select('*')
        .eq('id', id)
        .single();

      const { data, error } = await supabase
        .from('performance_analyze_groups')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;

      // Log audit
      const { data: userData } = await supabase.auth.getUser();
      await supabase.from('performance_analyze_audit_log').insert({
        action: 'update',
        group_id: id,
        old_config: oldData,
        new_config: data,
        changed_by: userData?.user?.id
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['performance-groups'] });
      toast.success('Group updated successfully');
    },
    onError: (error: any) => {
      toast.error('Failed to update group: ' + error.message);
    }
  });

  const deleteGroup = useMutation({
    mutationFn: async (groupId: string) => {
      // Get old config
      const { data: oldData } = await supabase
        .from('performance_analyze_groups')
        .select('*')
        .eq('id', groupId)
        .single();

      const { error } = await supabase
        .from('performance_analyze_groups')
        .delete()
        .eq('id', groupId);
      
      if (error) throw error;

      // Log audit
      const { data: userData } = await supabase.auth.getUser();
      await supabase.from('performance_analyze_audit_log').insert({
        action: 'delete',
        group_id: groupId,
        old_config: oldData,
        changed_by: userData?.user?.id
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['performance-groups'] });
      toast.success('Group deleted successfully');
    },
    onError: (error: any) => {
      toast.error('Failed to delete group: ' + error.message);
    }
  });

  return {
    groups: groups || [],
    isLoading,
    createGroup,
    updateGroup,
    deleteGroup,
    refetch
  };
};
